import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, IconButton, Typography, Button, Chip, Badge, Tooltip,
  Fade, Slide, Stack, ClickAwayListener, Paper, Divider,
  useTheme, useMediaQuery, Drawer, Skeleton
} from '@mui/material';
import {
  NotificationsNone as BellIcon,
  NotificationsActive as BellActiveIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Campaign as CampaignIcon,
  Info as InfoIcon,
  AccessTime as TimeIcon,
  Launch as LaunchIcon,
  DoneAll as DoneAllIcon
} from '@mui/icons-material';
import {
  getUserNotifications,
  markNotificationAsRead,
  trackNotificationClick,
  formatTimeLeft,
  getNotificationColor
} from '../../services/notificationService';

const brand = {
  primary: '#e31e24', gray50: '#f8fafc', gray100: '#f1f5f9',
  gray200: '#e2e8f0', gray400: '#94a3b8', gray500: '#64748b',
  gray600: '#475569', gray700: '#334155', gray800: '#1e293b',
};

const typeIcon = (type, size = 20) => {
  const props = { sx: { fontSize: size } };
  switch (type) {
    case 'success': return <CheckCircleIcon {...props} sx={{ ...props.sx, color: '#16a34a' }} />;
    case 'warning': return <WarningIcon {...props} sx={{ ...props.sx, color: '#ea580c' }} />;
    case 'error': return <ErrorIcon {...props} sx={{ ...props.sx, color: '#dc2626' }} />;
    case 'announcement': return <CampaignIcon {...props} sx={{ ...props.sx, color: '#7c3aed' }} />;
    default: return <InfoIcon {...props} sx={{ ...props.sx, color: '#0284c7' }} />;
  }
};

const NotificationSystem = ({
  page = 'dashboard',
  position = 'top-right',
  maxNotifications = 10,
  refreshInterval = 30000,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const dismissedRef = useRef(new Set());
  const timersRef = useRef([]);

  const fetchNotifications = useCallback(async (silent = false) => {
    try {
      if (!silent) setInitialLoad(true);
      const result = await getUserNotifications(page, false);
      if (result.success) {
        const active = (result.data || []).filter(n => !dismissedRef.current.has(n._id));
        setNotifications(active);
        setUnreadCount(result.unreadCount || 0);
      }
    } catch (err) {
      console.error('Notification fetch error:', err);
    } finally {
      setInitialLoad(false);
    }
  }, [page]);

  useEffect(() => {
    fetchNotifications(false);
    const interval = setInterval(() => fetchNotifications(true), refreshInterval);
    return () => {
      clearInterval(interval);
      timersRef.current.forEach(clearTimeout);
    };
  }, [fetchNotifications, refreshInterval]);

  const handleDismiss = useCallback((id) => {
    dismissedRef.current.add(id);
    setNotifications(prev => prev.filter(n => n._id !== id));
  }, []);

  const handleMarkRead = useCallback(async (id) => {
    try {
      const result = await markNotificationAsRead(id);
      if (result.success) {
        setNotifications(prev => prev.map(n => n._id === id ? { ...n, isRead: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) { console.error(err); }
  }, []);

  const handleMarkAllRead = useCallback(async () => {
    const unread = notifications.filter(n => !n.isRead);
    await Promise.all(unread.map(n => markNotificationAsRead(n._id)));
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    setUnreadCount(0);
  }, [notifications]);

  const handleAction = useCallback(async (notification) => {
    try {
      if (notification.actionUrl) {
        await trackNotificationClick(notification._id);
        if (notification.actionUrl.startsWith('http')) window.open(notification.actionUrl, '_blank');
        else window.location.href = notification.actionUrl;
      }
      if (!notification.isRead) await handleMarkRead(notification._id);
    } catch (err) { console.error(err); }
  }, [handleMarkRead]);

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return 'Vừa xong';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} phút trước`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} giờ trước`;
    if (diff < 172800000) return 'Hôm qua';
    return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' });
  };

  const NotifItem = ({ n, last }) => (
    <Box
      sx={{
        px: 2, py: 1.5,
        bgcolor: n.isRead ? 'transparent' : `${brand.primary}04`,
        transition: 'background 0.15s',
        cursor: 'pointer',
        '&:hover': { bgcolor: brand.gray100 },
        borderBottom: last ? 'none' : `1px solid ${brand.gray200}`,
      }}
      onClick={() => { if (!n.isRead) handleMarkRead(n._id); }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box sx={{
          mt: 0.25, p: 0.75, borderRadius: 1.5,
          bgcolor: `${getNotificationColor(n.type)}12`,
          display: 'flex', flexShrink: 0,
        }}>
          {typeIcon(n.type, 18)}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.25 }}>
            <Typography variant="body2" sx={{
              fontWeight: n.isRead ? 500 : 700, color: brand.gray800,
              fontSize: '0.85rem', lineHeight: 1.3,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {n.title}
            </Typography>
            {!n.isRead && (
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: brand.primary, flexShrink: 0 }} />
            )}
          </Stack>

          <Typography variant="caption" sx={{
            color: brand.gray600, display: '-webkit-box',
            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            lineHeight: 1.4, fontSize: '0.78rem',
          }}>
            {n.message}
          </Typography>

          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.75 }}>
            <Typography variant="caption" sx={{ color: brand.gray400, fontSize: '0.7rem' }}>
              {formatDate(n.createdAt)}
            </Typography>

            {n.priority === 'urgent' && (
              <Chip label="Khẩn" size="small" sx={{
                height: 18, fontSize: '0.6rem', fontWeight: 700,
                bgcolor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca',
              }} />
            )}
            {n.priority === 'high' && (
              <Chip label="Quan trọng" size="small" sx={{
                height: 18, fontSize: '0.6rem', fontWeight: 700,
                bgcolor: '#fff7ed', color: '#ea580c', border: '1px solid #fed7aa',
              }} />
            )}
            {n.timeLeftMs > 0 && (
              <Chip icon={<TimeIcon sx={{ fontSize: '12px !important' }} />} label={formatTimeLeft(n.timeLeftMs)}
                size="small" sx={{
                  height: 18, fontSize: '0.6rem', color: brand.gray500,
                  '& .MuiChip-icon': { ml: 0.5 },
                }} />
            )}
          </Stack>

          {n.actionUrl && n.actionText && (
            <Button size="small" endIcon={<LaunchIcon sx={{ fontSize: '14px !important' }} />}
              onClick={(e) => { e.stopPropagation(); handleAction(n); }}
              sx={{
                mt: 0.75, textTransform: 'none', fontSize: '0.75rem',
                fontWeight: 600, color: getNotificationColor(n.type),
                p: '2px 8px', minHeight: 0, borderRadius: 1,
                '&:hover': { bgcolor: `${getNotificationColor(n.type)}10` },
              }}>
              {n.actionText}
            </Button>
          )}
        </Box>

        <IconButton size="small"
          onClick={(e) => { e.stopPropagation(); handleDismiss(n._id); }}
          sx={{ p: 0.5, color: brand.gray400, '&:hover': { color: brand.gray600 } }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Stack>
    </Box>
  );

  const panelContent = (
    <Box sx={{ width: isMobile ? '100%' : 380, maxHeight: isMobile ? '85vh' : '70vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${brand.gray200}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: brand.gray800 }}>
          Thông báo
          {unreadCount > 0 && (
            <Typography component="span" sx={{ ml: 1, color: brand.primary, fontWeight: 700, fontSize: '0.85rem' }}>
              ({unreadCount} mới)
            </Typography>
          )}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          {unreadCount > 0 && (
            <Tooltip title="Đọc tất cả">
              <IconButton size="small" onClick={handleMarkAllRead} sx={{ color: brand.primary }}>
                <DoneAllIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}
          {isMobile && (
            <IconButton size="small" onClick={() => setOpen(false)}>
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </Stack>
      </Box>

      {/* List */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {initialLoad ? (
          <Stack spacing={0} sx={{ p: 0 }}>
            {[1, 2, 3].map(i => (
              <Box key={i} sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${brand.gray200}` }}>
                <Stack direction="row" spacing={1.5}>
                  <Skeleton variant="rounded" width={32} height={32} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton width="70%" height={18} />
                    <Skeleton width="90%" height={14} sx={{ mt: 0.5 }} />
                    <Skeleton width="30%" height={12} sx={{ mt: 0.5 }} />
                  </Box>
                </Stack>
              </Box>
            ))}
          </Stack>
        ) : notifications.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
            <BellIcon sx={{ fontSize: 48, color: brand.gray200, mb: 1 }} />
            <Typography variant="body2" sx={{ color: brand.gray400, fontWeight: 500 }}>
              Không có thông báo nào
            </Typography>
          </Box>
        ) : (
          notifications.slice(0, maxNotifications).map((n, i) => (
            <NotifItem key={n._id} n={n} last={i === Math.min(notifications.length, maxNotifications) - 1} />
          ))
        )}
      </Box>

      {notifications.length > maxNotifications && (
        <Box sx={{ px: 2, py: 1, borderTop: `1px solid ${brand.gray200}`, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: brand.gray500 }}>
            +{notifications.length - maxNotifications} thông báo khác
          </Typography>
        </Box>
      )}
    </Box>
  );

  const bellButton = (
    <Tooltip title={unreadCount > 0 ? `${unreadCount} thông báo mới` : 'Thông báo'}>
      <IconButton
        onClick={() => setOpen(prev => !prev)}
        sx={{
          color: open ? brand.primary : brand.gray600,
          transition: 'all 0.2s',
          '&:hover': { bgcolor: `${brand.primary}08` },
        }}
      >
        <Badge
          badgeContent={unreadCount}
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              bgcolor: brand.primary, color: 'white',
              fontSize: '0.65rem', fontWeight: 700,
              minWidth: 18, height: 18,
              ...(unreadCount > 0 && { animation: 'notifBounce 0.4s ease' }),
            },
          }}
        >
          {unreadCount > 0 ? <BellActiveIcon /> : <BellIcon />}
        </Badge>
      </IconButton>
    </Tooltip>
  );

  if (isMobile) {
    return (
      <>
        <style>{`@keyframes notifBounce { 0%,100%{transform:scale(1)} 50%{transform:scale(1.2)} }`}</style>
        {bellButton}
        <Drawer
          anchor="right" open={open}
          onClose={() => setOpen(false)}
          PaperProps={{ sx: { width: '85vw', maxWidth: 400, borderRadius: '16px 0 0 16px' } }}
        >
          {panelContent}
        </Drawer>
      </>
    );
  }

  return (
    <>
      <style>{`@keyframes notifBounce { 0%,100%{transform:scale(1)} 50%{transform:scale(1.2)} }`}</style>
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        {bellButton}
        {open && (
          <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Fade in>
              <Paper
                elevation={0}
                sx={{
                  position: 'absolute', top: 44, right: 0,
                  borderRadius: 3, overflow: 'hidden',
                  border: `1px solid ${brand.gray200}`,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
                  zIndex: 1300,
                }}
              >
                {panelContent}
              </Paper>
            </Fade>
          </ClickAwayListener>
        )}
      </Box>
    </>
  );
};

export default NotificationSystem;
